import type { UiMenuItem} from '@tyndo/types';

/**
 * A node in the resolved menu tree emitted by {@link MenuRegistry.getTree} /
 * {@link MenuRegistry.getVisibleMenus}: a menu item paired with its owning
 * module id and its already-resolved child nodes. Exported so shell nav code
 * (AppShell's sidebar bucketing / icon resolution / active-node walk) can type
 * the tree it consumes instead of falling back to `any[]`.
 */
export interface MenuTreeNode {
 item: UiMenuItem;
 moduleId: string;
 children: MenuTreeNode[];
}

class MenuRegistry {
 private items = new Map<string, UiMenuItem & { childIds: string[]; moduleId: string}>();

 /**
  * Register a module's menu items.
  *
  * `mode` makes the two-pass federation flow explicit:
  *   - `'append'` (default): merge `items` into the registry, overwriting any
  *     entry that shares a menuId. This preserves the historical Pass 1
  *     behaviour (gateway-stub menus) and is also correct for additive
  *     registrations.
  *   - `'replace'`: first drop every existing entry owned by `moduleId`, then
  *     register `items`. Pass 2 (federated bundle) uses this so the module's
  *     authoritative menu tree REPLACES its stubs instead of leaving orphaned
  *     stub menuIds that the federated tree didn't reuse (which previously
  *     doubled the module's top-level nav). Equivalent to
  *     `removeForModule(moduleId)` followed by `register(moduleId, items)`,
  *     in one explicit call.
  */
 register(moduleId: string, items: UiMenuItem[], mode: 'append' | 'replace' = 'append'): void {
 if (mode === 'replace') {
 this.removeForModule(moduleId);
 }
 for (const item of items) {
 const entry: UiMenuItem & { childIds: string[]; moduleId: string} = {
 ...item,
 childIds: [],
 moduleId,
};
 // menuId is optional on UiMenuItem (manifest-style field). Skip
 // entries without one — they came from a non-manifest source and
 // can't participate in the parent-child tree.
 if (!item.menuId) continue;
 this.items.set(item.menuId, entry);
}

 // Link children to parents
 for (const item of items) {
 if (item.parentMenuId && item.menuId) {
 const parent = this.items.get(item.parentMenuId);
 if (parent) {
 parent.childIds.push(item.menuId);
}
}
}
}

 getTree(): MenuTreeNode[] {
 const roots: MenuTreeNode[] = [];
 const visited = new Set<string>();

 // Track the active recursion path so a parent→child cycle (A→B→A) is
 // detected and reported rather than silently swallowed. `visited` alone
 // can't distinguish a re-entry caused by a cycle from a node that simply
 // appears once: the path set is reset as the recursion unwinds, so a hit
 // here means the current branch loops back on itself.
 const path = new Set<string>();

 const buildNode = (menuId: string): MenuTreeNode | null => {
 if (path.has(menuId)) {
 console.warn(
 `Menu cycle detected: '${menuId}' is its own ancestor (parent-child loop). Skipping the back-edge.`,
 );
 return null;
}
 if (visited.has(menuId)) return null;
 visited.add(menuId);
 path.add(menuId);

 const entry = this.items.get(menuId);
 if (!entry) {
 path.delete(menuId);
 return null;
}

 const children: MenuTreeNode[] = [];
 for (const childId of entry.childIds) {
 const childNode = buildNode(childId);
 if (childNode) children.push(childNode);
}

 path.delete(menuId);
 return {
 item: entry,
 moduleId: entry.moduleId,
 children,
};
};

 // Start with items that have no parent
 for (const [menuId, item] of this.items.entries()) {
 if (!item.parentMenuId) {
 const node = buildNode(menuId);
 if (node) roots.push(node);
}
}

 // Surface orphaned cycles: a group of items whose parentMenuId links form a
 // closed loop with no acyclic entry point (e.g. A→B→A where neither is a
 // root) is never reached by the root walk above, so it would silently vanish
 // from the tree. Detect any unvisited item whose chain of parents loops back
 // on itself and warn so manifest authors get visibility instead of a missing
 // menu.
 for (const [menuId, item] of this.items.entries()) {
 if (visited.has(menuId) || !item.parentMenuId) continue;
 const chain = new Set<string>();
 let cursor: string | undefined = menuId;
 while (cursor && !chain.has(cursor)) {
 chain.add(cursor);
 cursor = this.items.get(cursor)?.parentMenuId;
}
 if (cursor && chain.has(cursor)) {
 console.warn(
 `Menu cycle detected: '${menuId}' is part of a parent-child loop with no root. This subtree is omitted from the menu.`,
 );
 // Mark the whole loop visited so we warn once per cycle, not per node.
 for (const id of chain) visited.add(id);
}
}

 // Sort by sequence
 const sortBySequence = (nodes: MenuTreeNode[]): MenuTreeNode[] => {
 return nodes
 .sort((a, b) => (a.item.sequence ?? 0) - (b.item.sequence ?? 0))
 .map((node) => ({
 ...node,
 children: sortBySequence(node.children),
}));
};

 return sortBySequence(roots);
}

 getItem(menuId: string): (UiMenuItem & { moduleId: string }) | null {
 const item = this.items.get(menuId);
 return item || null;
}

 getVisibleMenus(userRoles: string[]): MenuTreeNode[] {
 const filterByRoles = (nodes: MenuTreeNode[]): MenuTreeNode[] => {
 return nodes
 .filter((node) => {
 if (!node.item.requiredRoles || node.item.requiredRoles.length === 0) {
 return true;
}
 return node.item.requiredRoles.some((role) => userRoles.includes(role));
})
 .map((node) => ({
 ...node,
 children: filterByRoles(node.children),
}));
};

 return filterByRoles(this.getTree());
}

 clear(): void {
 this.items.clear();
}

 /**
  * Remove all menu entries belonging to a given module. Used when the federated
  * module loads after the gateway manifest's stub registration so the
  * authoritative federated menus replace (rather than duplicate) the stubs.
  */
 removeForModule(moduleId: string): void {
 const removed: string[] = [];
 for (const [id, entry] of this.items.entries()) {
 if (entry.moduleId === moduleId) {
 removed.push(id);
}
}
 for (const id of removed) {
 this.items.delete(id);
}
 // Strip removed child ids from any surviving parent entries. `childIds` is
 // the internal menuId list (kept distinct from UiMenuItem.children so the
 // two never collide in the type).
 for (const entry of this.items.values()) {
 entry.childIds = entry.childIds.filter((cid) => !removed.includes(cid));
}
}
}

export const menuRegistry = new MenuRegistry();
